"use client";

import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { OrderOutput, OrderProductOutput } from "@/interfaces";
import { numberToMoney } from "@/util/moneyConversion";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  Calculator,
} from "lucide-react";

interface OrderMarginAnalysisProps {
  order: { products?: OrderProductOutput[] } | OrderOutput;
  additional?: number;
  discount?: number;
  className?: string;
}

export default function OrderMarginAnalysis({
  order,
  additional = 0,
  discount = 0,
  className = "",
}: OrderMarginAnalysisProps) {
  const analysis = useMemo(() => {
    if (!order.products || order.products.length === 0) {
      return {
        totalRevenue: 0,
        totalCost: 0,
        totalMargin: 0,
        marginPercentage: 0,
        isPositive: false,
        productCount: 0,
        baseProductsValue: 0,
        additionalsValue: 0,
        finalTotal: 0,
      };
    }

    let baseProductsRevenue = 0;
    let additionalsRevenue = 0;
    let totalCost = 0;

    order.products.forEach((product) => {
      const revenue = (product.unitPrice || 0) * (product.quantity || 1);
      const cost = (product.unitCost || 0) * (product.quantity || 1);

      baseProductsRevenue += revenue;
      totalCost += cost;

      // Incluir adicionais no cálculo se existirem
      if (product.additionals && Array.isArray(product.additionals)) {
        product.additionals.forEach((additional) => {
          const additionalRevenue =
            (additional.unitPrice || 0) * (additional.quantity || 1);
          const additionalCost =
            (additional.unitCost || 0) * (additional.quantity || 1);

          additionalsRevenue += additionalRevenue;
          totalCost += additionalCost;
        });
      }
    });

    // Calcular valores finais
    const subtotal = baseProductsRevenue + additionalsRevenue;
    const totalRevenue = subtotal + additional - discount;
    const finalTotal = totalRevenue;

    const totalMargin = totalRevenue - totalCost;
    const marginPercentage =
      totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;

    return {
      totalRevenue,
      totalCost,
      totalMargin,
      marginPercentage,
      isPositive: totalMargin >= 0,
      productCount: order.products.length,
      baseProductsValue: baseProductsRevenue,
      additionalsValue: additionalsRevenue,
      finalTotal,
    };
  }, [order.products, additional, discount]);

  const getMarginColor = (isPositive: boolean) => {
    return isPositive ? "text-green-600" : "text-red-600";
  };

  const getMarginBgColor = (isPositive: boolean) => {
    return isPositive
      ? "bg-green-50 border-green-200"
      : "bg-red-50 border-red-200";
  };

  const getMarginIcon = (isPositive: boolean) => {
    return isPositive ? TrendingUp : TrendingDown;
  };

  const MarginIcon = getMarginIcon(analysis.isPositive);

  return (
    <Card className={`${className} ${getMarginBgColor(analysis.isPositive)}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Calculator className="h-5 w-5 text-gray-600" />
          Análise de Margem do Pedido
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Resumo Financeiro Principal */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Receita e Custo */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Composição Financeira</h4>
            
            <div className="space-y-2">
              {/* Produtos Base */}
              <div className="flex justify-between items-center p-2 bg-white rounded border">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700">Produtos Base</span>
                  <span className="text-xs text-gray-400">({analysis.productCount} item{analysis.productCount !== 1 ? 's' : ''})</span>
                </div>
                <span className="font-semibold text-sm">{numberToMoney(analysis.baseProductsValue)}</span>
              </div>

              {/* Adicionais */}
              {analysis.additionalsValue > 0 && (
                <div className="flex justify-between items-center p-2 bg-white rounded border">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-gray-700">Adicionais</span>
                  </div>
                  <span className="font-semibold text-sm text-blue-600">+{numberToMoney(analysis.additionalsValue)}</span>
                </div>
              )}

              {/* Taxa Adicional */}
              {additional > 0 && (
                <div className="flex justify-between items-center p-2 bg-white rounded border">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">Taxa Adicional</span>
                  </div>
                  <span className="font-semibold text-sm text-green-600">+{numberToMoney(additional)}</span>
                </div>
              )}

              {/* Desconto */}
              {discount > 0 && (
                <div className="flex justify-between items-center p-2 bg-white rounded border">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-gray-700">Desconto</span>
                  </div>
                  <span className="font-semibold text-sm text-red-600">-{numberToMoney(discount)}</span>
                </div>
              )}

              {/* Total da Receita */}
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-700">Receita Total</span>
                </div>
                <span className="font-bold text-lg text-blue-700">{numberToMoney(analysis.totalRevenue)}</span>
              </div>

              {/* Custo Total */}
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-gray-600" />
                  <span className="font-medium text-gray-700">Custo Total</span>
                </div>
                <span className="font-bold text-lg text-gray-700">{numberToMoney(analysis.totalCost)}</span>
              </div>
            </div>
          </div>

          {/* Análise de Margem */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Análise de Rentabilidade</h4>
            
            {/* Margem Principal */}
            <div className={`p-4 rounded-lg border-2 ${
              analysis.isPositive 
                ? "bg-green-50 border-green-200" 
                : "bg-red-50 border-red-200"
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MarginIcon className={`h-6 w-6 ${getMarginColor(analysis.isPositive)}`} />
                  <span className="font-medium text-gray-900">Margem de Lucro</span>
                </div>
                <Badge
                  variant="secondary"
                  className={`text-lg font-bold px-3 py-1 ${
                    analysis.isPositive
                      ? "bg-green-100 text-green-700 border-green-300"
                      : "bg-red-100 text-red-700 border-red-300"
                  }`}
                >
                  {analysis.marginPercentage.toFixed(1)}%
                </Badge>
              </div>
              
              <div className="text-center">
                <div className={`text-2xl font-bold ${getMarginColor(analysis.isPositive)}`}>
                  {numberToMoney(analysis.totalMargin)}
                </div>
                <div className={`text-sm ${getMarginColor(analysis.isPositive)}`}>
                  {analysis.isPositive ? "Lucro" : "Prejuízo"}
                </div>
              </div>
            </div>

            {/* Indicador Visual */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Margem sobre receita</span>
                <span className={`font-medium ${getMarginColor(analysis.isPositive)}`}>
                  {analysis.marginPercentage.toFixed(2)}%
                </span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-300 ${
                    analysis.isPositive ? "bg-green-500" : "bg-red-500"
                  }`}
                  style={{
                    width: `${Math.min(Math.abs(analysis.marginPercentage), 100)}%`,
                  }}
                />
              </div>

              <div className="flex justify-between text-xs text-gray-500">
                <span>0%</span>
                <span>100%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Alertas */}
        {analysis.marginPercentage < 10 && analysis.isPositive && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-800">
              <TrendingDown className="h-4 w-4" />
              <span className="text-sm font-medium">Margem baixa</span>
            </div>
            <p className="text-xs text-yellow-700 mt-1">
              A margem está abaixo de 10%. Considere revisar os preços ou
              custos.
            </p>
          </div>
        )}

        {!analysis.isPositive && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-800">
              <TrendingDown className="h-4 w-4" />
              <span className="text-sm font-medium">Pedido com prejuízo</span>
            </div>
            <p className="text-xs text-red-700 mt-1">
              Este pedido está gerando prejuízo. Revise os preços dos produtos.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
